import {
  BookOpen,
  Building2,
  LifeBuoy,
  Lock,
  MessagesSquare,
  Search,
} from "lucide-react";
import { useDeferredValue, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGlobalSearch } from "@/features/search/search";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

/**
 * Global ⌘K search across members, discussions, resources, and help docs
 * (results are role-scoped server-side).
 *
 * `withTrigger={false}` mounts the palette and its shortcut with no visible
 * button: the member shell's top bar carries no search box, because the
 * directory now has its own, in place, above its results. The palette still
 * earns its keep for what that box cannot reach — threads, resources, help.
 */
export function GlobalSearch({ withTrigger = true }: { withTrigger?: boolean }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const { data: results } = useGlobalSearch(open ? deferredQuery : "");

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  function go(href: string) {
    setOpen(false);
    setQuery("");
    navigate(href);
  }

  const hasResults =
    results &&
    (results.members.length > 0 ||
      results.organizations.length > 0 ||
      results.threads.length > 0 ||
      results.resources.length > 0 ||
      results.docs.length > 0);

  return (
    <>
      {withTrigger && (
        <Button
          variant="outline"
          size="sm"
          className="text-muted-foreground w-full justify-start sm:w-64"
          onClick={() => setOpen(true)}
        >
          <Search data-slot="icon" />
          Search…
          <kbd className="bg-muted pointer-events-none ml-auto rounded px-1.5 font-mono text-[10px] font-medium">
            ⌘K
          </kbd>
        </Button>
      )}

      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        title="Search AFELL"
        description="Search members, discussions, resources, and help"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search members, discussions, resources, help…"
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            {query.trim().length >= 2 && results && !hasResults && (
              <CommandEmpty>No results for &ldquo;{query}&rdquo;</CommandEmpty>
            )}
            {query.trim().length < 2 && (
              <CommandEmpty>
                Type at least two characters to search.
              </CommandEmpty>
            )}

            {results && results.members.length > 0 && (
              <CommandGroup heading="Members">
                {results.members.map((m) => (
                  <CommandItem
                    key={m.id}
                    value={`member-${m.id}`}
                    onSelect={() => go(`/directory/${m.id}`)}
                  >
                    <Avatar className="size-5">
                      <AvatarImage src={m.image_url ?? undefined} alt={m.name} />
                      <AvatarFallback className="text-[10px]">
                        {m.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <span>{m.name}</span>
                    {m.org && (
                      <span className="text-muted-foreground truncate text-xs">
                        {m.org}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {results && results.organizations.length > 0 && (
              <CommandGroup heading="Organizations">
                {results.organizations.map((o) => (
                  <CommandItem
                    key={o.id}
                    value={`organization-${o.id}`}
                    onSelect={() =>
                      go(
                        `/directory?tab=organizations&search=${encodeURIComponent(o.name)}`
                      )
                    }
                  >
                    <Building2 className="size-4" />
                    <span className="truncate">{o.name}</span>
                    {o.location && (
                      <span className="text-muted-foreground truncate text-xs">
                        {o.location}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {results && results.threads.length > 0 && (
              <CommandGroup heading="Discussions">
                {results.threads.map((t) => (
                  <CommandItem
                    key={t.id}
                    value={`thread-${t.id}`}
                    onSelect={() => go(`/discussions/${t.id}`)}
                  >
                    <MessagesSquare className="size-4" />
                    <span className="truncate">{t.title}</span>
                    {t.via === "reply" && (
                      <span className="text-muted-foreground text-xs">
                        matched a reply
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {results && results.resources.length > 0 && (
              <CommandGroup heading="Resources">
                {results.resources.map((r) => (
                  <CommandItem
                    key={r.id}
                    value={`resource-${r.id}`}
                    onSelect={() => go("/resources")}
                  >
                    {r.locked ? (
                      <Lock className="size-4" />
                    ) : (
                      <BookOpen className="size-4" />
                    )}
                    <span className="truncate">{r.title}</span>
                    <span className="text-muted-foreground text-xs">
                      {r.category}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {results && results.docs.length > 0 && (
              <CommandGroup heading="Help">
                {results.docs.map((d) => (
                  <CommandItem
                    key={d.id}
                    value={`doc-${d.id}`}
                    onSelect={() => go(`/help/${d.slug}`)}
                  >
                    <LifeBuoy className="size-4" />
                    <span className="truncate">{d.title}</span>
                    <span className="text-muted-foreground text-xs">
                      {d.section}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  );
}

export default GlobalSearch;
